'use client';
import { useEffect } from 'react';

/* ── Shell ───────────────────────────────────────────────────────────────────
   Generic bottom sheet container.  All content (header + body) passed as children.
   ─────────────────────────────────────────────────────────────────────────── */
export const MobileBottomSheet = ({ visible, onHide, children }) => {
    useEffect(() => {
        if (visible) document.body.style.overflow = 'hidden';
        else         document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [visible]);

    if (!visible) return null;

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            {/* Backdrop */}
            <div
                onClick={onHide}
                style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(4px)',
                }}
            />

            {/* Sheet */}
            <div style={{
                position: 'relative', zIndex: 1,
                background: '#ffffff',
                borderRadius: '20px 20px 0 0',
                maxHeight: '88vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 -12px 48px rgba(0,0,0,0.25)',
                animation: 'mbs-up 0.3s cubic-bezier(0.32,0.72,0,1)',
                overflow: 'hidden',
            }}>
                {children}
            </div>
        </div>
    );
};

/* ── Standard bg-primary header with drag handle ─────────────────────────────
   Usage (standard icon/title):
     <BottomSheetHeader icon="pi-bolt" title="Actions" subtitle="#123" onClose={fn} />

   Usage (custom left slot):
     <BottomSheetHeader onClose={fn}>
       <div>…your content…</div>
     </BottomSheetHeader>
   ─────────────────────────────────────────────────────────────────────────── */
export const BottomSheetHeader = ({ icon, title, subtitle, onClose, children }) => (
    <div style={{ background: 'var(--primary-color)', borderRadius: '20px 20px 0 0', paddingBottom: 14, flexShrink: 0 }}>
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.35)' }} />
        </div>

        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px' }}>
            {/* Left: custom children OR icon + title + subtitle */}
            {children || (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {icon && (
                        <div style={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            background: 'rgba(255,255,255,0.2)',
                            border: '1.5px solid rgba(255,255,255,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className={`pi ${icon}`} style={{ color: 'white', fontSize: '0.9rem' }} />
                        </div>
                    )}
                    <div>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: 'white', lineHeight: 1.3 }}>
                            {title}
                        </p>
                        {subtitle && (
                            <p style={{ margin: 0, fontSize: '0.64rem', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Close button */}
            <button
                onClick={onClose}
                style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', marginLeft: 12,
                }}
            >
                <i className="pi pi-times" style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)' }} />
            </button>
        </div>
    </div>
);
